import axios from 'axios';
import router from '../router';

// 生产环境默认直连 /backend/api，避免未配置 /api 反向代理时注册、登录失败。
// 如已自行配置网关，也可在页面中通过 window.__SMARTSTUDY_API_BASE__ 覆盖。
const runtimeBaseURL =
  (typeof window !== 'undefined' && window.__SMARTSTUDY_API_BASE__) ||
  (process.env.NODE_ENV === 'production' ? '/backend/api' : '/api');

const service = axios.create({
  baseURL: runtimeBaseURL,
  timeout: 60000
});

service.interceptors.request.use((config) => {
  const token = localStorage.getItem('smartstudy_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

service.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response && error.response.status;
    const responseData = error.response && error.response.data;

    if (status === 401) {
      localStorage.removeItem('smartstudy_token');
      localStorage.removeItem('smartstudy_user');
      router.replace('/login');
    }

    if (responseData && typeof responseData === 'object') {
      return Promise.reject({
        ...responseData,
        message: responseData.message || '请求失败，请稍后重试'
      });
    }

    if (status) {
      return Promise.reject({
        code: status,
        message: `接口请求失败（HTTP ${status}），请检查宝塔站点中的 /api 或 /backend/api 配置。`
      });
    }

    return Promise.reject({
      code: 0,
      message: '网络请求失败，请检查后端服务是否可访问，或确认前端接口地址是否配置正确。'
    });
  }
);

export const authAPI = {
  login: (data) => service.post('/auth/login.php', data),
  register: (data) => service.post('/auth/register.php', data),
  getProfile: () => service.get('/auth/profile.php'),
  updateProfile: (data) => service.post('/auth/profile.php', data),
  exchangeTokens: (data) => service.post('/auth/exchange.php', data),
  sendResetEmail: (data) => service.post('/auth/send-reset-email.php', data),
  resetPassword: (data) => service.post('/auth/reset-password.php', data)
};

export const subjectAPI = {
  getList: () => service.get('/subjects/list.php')
};

export const chatAPI = {
  // AI 对话请求增加超时时间和重试
  send: (data) => service.post('/chat/send.php', data, { timeout: 120000 }),
  getSessions: (params) => service.get('/chat/sessions.php', { params }),
  getHistory: (params) => service.get('/chat/history.php', { params }),
  deleteSession: (data) => service.delete('/chat/delete.php', { data })
};

export const weakPointAPI = {
  getList: (params) => service.get('/weakpoints/list.php', { params }),
  merge: (data) => service.post('/weakpoints/merge.php', data)
};

export const quizAPI = {
  generate: (data) => service.post('/quiz/generate.php', data),
  submit: (data) => service.post('/quiz/submit.php', data)
};

export const parentAPI = {
  // 家长端首页数据
  getDashboard: () => service.get('/parent/dashboard.php'),
  // 学情报告
  getReport: (params) => service.get('/parent/report.php', { params }),
  // 获取学科列表
  getSubjects: () => service.get('/subjects/list.php'),
  // 获取薄弱点列表
  getWeakPoints: (params) => service.get('/weakpoints/list.php', { params }),
  // 生成测试题
  generateQuiz: (data) => service.post('/parent/generate-quiz.php', data),
  // 生成教案
  generateTeachingPlan: (data) => service.post('/parent/generate-plan.php', data)
};

export const courseAPI = {
  // 获取课程列表
  getList: (subjectId) => service.get('/courses/list.php', { params: { subject_id: subjectId } }),
  // 获取课程详情
  getDetail: (courseId) => service.get('/courses/detail.php', { params: { course_id: courseId } }),
  // 获取章节列表
  getChapters: (courseId) => service.get('/courses/chapters.php', { params: { course_id: courseId } }),
  // 获取章节内容
  getChapterContent: (chapterId) => service.get('/courses/chapter-content.php', { params: { chapter_id: chapterId } }),
  // 学习章节
  learnChapter: (chapterId) => service.post('/courses/learn.php', { chapter_id: chapterId }),
  // 完成章节
  completeChapter: (chapterId) => service.post('/courses/complete.php', { chapter_id: chapterId }),
  // 解析PDF
  parsePDF: (formData) => service.post('/courses/parse-pdf-simple.php', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // 生成课程（AI生成）
  generateCourse: (formData) => service.post('/courses/generate.php', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000 // 5分钟超时
  })
};

export const adminCourseAPI = {
  // 获取课程列表（管理后台）
  getList: (params) => service.get('/admin/courses/list.php', { params }),
  // 创建课程
  create: (data) => service.post('/admin/courses/create.php', data),
  // 更新课程
  update: (data) => service.post('/admin/courses/update.php', data),
  // 删除课程
  delete: (id) => service.post('/admin/courses/delete.php', { id }),
  // 获取章节列表
  getChapters: (courseId) => service.get('/admin/courses/chapters.php', { params: { course_id: courseId } }),
  // 创建章节
  createChapter: (data) => service.post('/admin/courses/chapters.php', data),
  // 更新章节
  updateChapter: (data) => service.put('/admin/courses/chapters.php', data),
  // 删除章节
  deleteChapter: (id) => service.delete('/admin/courses/chapters.php', { data: { id } })
};

export default service;